function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }
function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i]; return arr2; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
import React, { useEffect, useMemo } from 'react';
import makeCancellable from 'make-cancellable-promise';
import invariant from 'tiny-invariant';
import warning from 'warning';
import pdfjs from "../pdfjs.js";
import usePageContext from "../shared/hooks/usePageContext.js";
import useResolver from "../shared/hooks/useResolver.js";
import { cancelRunningTask, isCancelException, makePageCallback } from "../shared/utils.js";
export default function PageSVG() {
  var pageContext = usePageContext();
  invariant(pageContext, 'Unable to find Page context.');
  var _className = pageContext._className,
    onRenderSuccessProps = pageContext.onRenderSuccess,
    onRenderErrorProps = pageContext.onRenderError,
    page = pageContext.page,
    rotate = pageContext.rotate,
    scale = pageContext.scale;
  invariant(page, 'Attempted to render page SVG, but no page was specified.');
  var _useResolver = useResolver(),
    _useResolver2 = _slicedToArray(_useResolver, 2),
    svgState = _useResolver2[0],
    svgDispatch = _useResolver2[1];
  var svg = svgState.value,
    svgError = svgState.error;
  /**
   * Called when a page is rendered successfully
   */
  function onRenderSuccess() {
    if (!page) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    if (onRenderSuccessProps) {
      onRenderSuccessProps(makePageCallback(page, scale));
    }
  }
  /**
   * Called when a page fails to render
   */
  function onRenderError() {
    if (!svgError) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    if (isCancelException(svgError)) {
      return;
    }
    warning(false, svgError.toString());
    if (onRenderErrorProps) {
      onRenderErrorProps(svgError);
    }
  }
  var viewport = useMemo(function () {
    return page.getViewport({
      scale: scale,
      rotation: rotate
    });
  }, [page, rotate, scale]);
  function resetSVG() {
    svgDispatch({
      type: 'RESET'
    });
  }
  useEffect(resetSVG, [page, svgDispatch, viewport]);
  function renderSVG() {
    if (!page) {
      return;
    }
    var cancellable = makeCancellable(page.getOperatorList());
    cancellable.promise.then(function (operatorList) {
      var svgGfx = new pdfjs.SVGGraphics(page.commonObjs, page.objs);
      svgGfx.getSVG(operatorList, viewport).then(function (nextSvg) {
        // See https://github.com/mozilla/pdf.js/issues/16745
        if (!(nextSvg instanceof SVGElement)) {
          throw new Error('getSVG returned unexpected result.');
        }
        svgDispatch({
          type: 'RESOLVE',
          value: nextSvg
        });
      }).catch(function (error) {
        svgDispatch({
          type: 'REJECT',
          error: error
        });
      });
    }).catch(function (error) {
      svgDispatch({
        type: 'REJECT',
        error: error
      });
    });
    return function () {
      return cancelRunningTask(cancellable);
    };
  }
  useEffect(renderSVG, [page, svgDispatch, viewport]);
  useEffect(function () {
    if (svg === undefined) {
      return;
    }
    if (svg === false) {
      onRenderError();
      return;
    }
    onRenderSuccess();
  },
  // Ommitted callbacks so they are not called every time they change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [svg]);
  function drawPageOnContainer(element) {
    if (!element || !svg) {
      return;
    }
    // Append SVG element to the main container, if this hasn't been done already
    if (!element.firstElementChild) {
      element.appendChild(svg);
    }
    var width = viewport.width,
      height = viewport.height;
    svg.setAttribute('width', "".concat(width));
    svg.setAttribute('height', "".concat(height));
  }
  var width = viewport.width,
    height = viewport.height;
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(_className, "__svg"),
    // Note: This cannot be shortened, as we need this function to be called with each render.
    ref: function ref(_ref) {
      return drawPageOnContainer(_ref);
    },
    style: {
      display: 'block',
      backgroundColor: 'white',
      overflow: 'hidden',
      width: width,
      height: height,
      userSelect: 'none'
    }
  });
}